<template>
  <v-card id="eudr-report-dialog">
    <v-card-title class="primary white--text">
      <v-row dense>
        <v-col>
          <span class="headline">
            {{ $t('eudrReport') }}
          </span>
        </v-col>
        <v-col cols="auto">
          <BaseDialogActions hideFullscreen hideRefresh/>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text>
      <v-container fluid class="pt-5">
        <v-row>
          <v-col cols="12">
            <AccountAutocomplete
            elementId="destination"
            hideRefresh
            :title="$t('destination')"
            onlyInternal
            @account-chosen="accountChosen"
            required/>
          </v-col>

          <v-col cols="12" v-if="startDate">
            <DatePicker
            elementId="start-date"
            :dateLabel="$t('startDate')"
            :startDate="startDate"
            @date-picked="startDateChosen"/>
          </v-col>

          <v-col cols="12" v-if="endDate">
            <DatePicker
            elementId="end-date"
            :dateLabel="$t('endDate')"
            :startDate="endDate"
            @date-picked="endDateChosen"/>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <v-card-actions class="py-4 px-0">
      <v-row dense justify="end" align="center">
        <v-col>
          <span class="caption error--text" v-if="errorMessage">
            {{ errorMessage }}
          </span>
        </v-col>

        <v-col cols="auto">
          <Icon
          :small="false"
          icon="mdi-information-outline"
          :tooltipText="infoTooltip"/>
        </v-col>

        <v-col cols="auto">
          <v-btn color="primary"
          :disabled="!downloadEnabled"
          id="download"
          @click="downloadClicked">
            <span>
              {{ $t('downloadCsv') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import { localToCompanyOffsetEndOfDay } from '@/utils/DateFormatter'
import { generateCsvString } from '@/utils/CSVUtility'
import { saveAs } from 'file-saver'

const MAX_DAYS = 31

export default {
  name: 'TicketTractsByDestination',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    DatePicker: () => import('@/components/helper/DatePicker.vue'),
    AccountAutocomplete: () => import('@/components/autocomplete/AccountAutocomplete.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  data: () => ({
    destinationAccount: null,
    startDate: moment().startOf('day').subtract(1, 'week'),
    endDate: moment().endOf('day'),
    noTicketsFoundForSelection: false
  }),

  computed: {
    infoTooltip () {
      return this.$t('eudrReportInfoTooltip')
    },

    downloadEnabled () {
      return !!this.destinationAccount?.accountId &&
      !!this.startDate &&
      !!this.endDate &&
      !this.noTicketsFoundForSelection &&
      this.validTimeSpan
    },

    validTimeSpan () {
      return this.datesNotInFuture &&
      this.endDateNotBeforeStartDate &&
      this.timeSpanNotTooLong
    },

    datesNotInFuture () {
      return this.notInFuture(this.startDate) && this.notInFuture(this.endDate)
    },

    endDateNotBeforeStartDate () {
      return moment(this.startDate).isSameOrBefore(this.endDate)
    },

    timeSpanNotTooLong () {
      return moment(this.endDate).diff(moment(this.startDate), 'days') <= MAX_DAYS
    },

    errorMessage () {
      if (!this.datesNotInFuture) {
        return this.$t('mustNotBeInFuture')
      }
      if (!this.endDateNotBeforeStartDate) {
        return this.$t('startDateBeforeEndDate')
      }
      if (!this.timeSpanNotTooLong) {
        return this.$t('mustNotExceedNDays', { n: MAX_DAYS })
      }
      return null
    }
  },

  methods: {
    ...mapActions('ticket', ['fetchTicketTractsByDestination']),

    async downloadClicked () {
      const tracts = await this.fetchTicketTractsByDestination({
        destinationAccountId: this.destinationAccount.accountId,
        startDate: localToCompanyOffsetEndOfDay(moment(this.startDate).subtract(1, 'day').format()),
        endDate: localToCompanyOffsetEndOfDay(moment(this.endDate).format())
      })

      if (tracts.length === 0) {
        this.noTicketsFoundForSelection = true
        this.setSnack(this.$t('noCorrespondingTickets'))
      } else {
        const csvString = generateCsvString(tracts, [
          { label: 'tract', value: t => t.tractName },
          { label: 'latitude', value: t => t.latitude },
          { label: 'longitude', value: t => t.longitude },
          { label: 'harvestStartDate', value: t => moment(t.harvestStartDate).format('MM/DD/YYYY') },
          { label: 'harvestEndDate', value: t => moment(t.harvestEndDate).format('MM/DD/YYYY') }
        ], { emptyValue: this.$t('notAvailable') })
        const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
        saveAs(blob, `EUDR_${this.destinationAccount.name}_${this.startDate.format('YYYYMMDD')}_${this.endDate.format('YYYYMMDD')}.csv`)
      }
    },

    startDateChosen (date) {
      this.noTicketsFoundForSelection = false
      this.startDate = moment(date).startOf('day')
    },

    endDateChosen (date) {
      this.noTicketsFoundForSelection = false
      this.endDate = moment(date).endOf('day')
    },

    notInFuture (date) {
      return moment(date).isSameOrBefore(moment().endOf('day'))
    },

    accountChosen (account) {
      this.noTicketsFoundForSelection = false
      this.destinationAccount = account
    }
  }
}
</script>
